// The whole lesson, step by step, inside a device frame.
//
// Two widths because the app runs on both and the pitches behave
// differently at each: a nine-zone map that reads fine at 390 can put two
// labels on top of each other at a narrower width, and that is exactly
// the class of bug this is here to catch before a phone does.
//
// The widths are real device logical widths, not round numbers chosen for
// the layout — 390 is an iPhone 14/15, 360 the most common Android, 768
// an iPad in portrait.
import { useState } from "react";
import { Smartphone, Tablet } from "lucide-react";
import AssetPreview from "./AssetPreview";
import { PITCH_COLOR as C } from "./geometry";
import type { AcademyAdminConcept } from "@/api/services/academy.service";

const DEVICES = [
  { id: "android", label: "Android", width: 360, icon: Smartphone },
  { id: "iphone", label: "iPhone", width: 390, icon: Smartphone },
  { id: "tablet", label: "Tablet", width: 768, icon: Tablet },
] as const;

type DeviceId = (typeof DEVICES)[number]["id"];

export default function LessonPreview({
  concept,
}: {
  concept: AcademyAdminConcept;
}) {
  const [device, setDevice] = useState<DeviceId>("iphone");
  const [index, setIndex] = useState(0);

  const steps = concept.assets;
  const current = steps[Math.min(index, steps.length - 1)];
  const width = DEVICES.find((d) => d.id === device)!.width;

  // The tablet is wider than the dialog, so it is scaled down rather than
  // clipped. The pitches are drawn at the real width first and then
  // scaled, so proportions stay honest — what shrinks is the whole
  // screen, exactly as holding a tablet further away would.
  const maxWidth = 420;
  const scale = width > maxWidth ? maxWidth / width : 1;

  if (!steps.length) {
    return (
      <div className="rounded-xl border border-dashed p-10 text-center text-sm text-muted-foreground">
        This lesson has no steps yet. Add one and it will appear here.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 rounded-xl bg-muted p-1">
          {DEVICES.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => setDevice(d.id)}
              className={
                "flex cursor-pointer items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors " +
                (device === d.id
                  ? "bg-emerald-600 text-white shadow"
                  : "text-muted-foreground hover:text-foreground")
              }
            >
              <d.icon className="h-3.5 w-3.5" />
              {d.label}
            </button>
          ))}
        </div>
        <span className="text-[11px] text-muted-foreground">
          {width}px wide
          {scale < 1 ? ` · shown at ${Math.round(scale * 100)}%` : ""}
        </span>

        <div className="ml-auto flex items-center gap-1 overflow-x-auto">
          {steps.map((a, i) => (
            <button
              key={a.id}
              type="button"
              title={a.kindLabel}
              onClick={() => setIndex(i)}
              className={
                "h-7 w-7 shrink-0 cursor-pointer rounded text-[11px] font-bold transition-colors " +
                (i === index
                  ? "bg-emerald-600 text-white"
                  : "bg-muted text-muted-foreground hover:bg-muted/70")
              }
            >
              {a.order}
            </button>
          ))}
        </div>
      </div>

      <div className="flex justify-center">
        <div
          style={{
            width: width * scale,
            // The frame must reserve the UNSCALED height or the layout
            // leaves a gap the size of the difference underneath it.
            transformOrigin: "top center",
          }}
        >
          <div
            style={{
              width,
              transform: `scale(${scale})`,
              transformOrigin: "top center",
              marginBottom: scale < 1 ? 0 : undefined,
            }}
          >
            <DeviceChrome width={width}>
              <AssetPreview
                kind={current.kind}
                payload={current.payload as Record<string, unknown>}
                width={width}
              />
            </DeviceChrome>
          </div>
        </div>
      </div>

      <p className="text-center text-[11px] text-muted-foreground">
        Step {current.order} · {current.kindLabel}
        {current.isGradeable ? " · answer shown revealed" : ""}
      </p>
    </div>
  );
}

// The lesson screen's own frame: dark green ground, a progress bar and a
// lives counter at the top, a Continue button at the bottom. Not pixel
// identical to the app, and not trying to be — it is here so the content
// is judged in roughly the space it will really have.
function DeviceChrome({
  width,
  children,
}: {
  width: number;
  children: React.ReactNode;
}) {
  return (
    <div
      className="overflow-hidden rounded-[28px] border-4 shadow-xl"
      style={{
        width,
        borderColor: "#1a1a1a",
        background: "#0B3B22",
      }}
    >
      <div className="flex items-center gap-2 px-4 pb-2 pt-4">
        <span
          className="text-base leading-none"
          style={{ color: "rgba(255,255,255,0.5)" }}
        >
          ✕
        </span>
        <div
          className="h-2 flex-1 overflow-hidden rounded-full"
          style={{ background: "rgba(255,255,255,0.15)" }}
        >
          <div
            className="h-full rounded-full"
            style={{ width: "35%", background: "#22C55E" }}
          />
        </div>
        <span
          className="rounded-full px-2 py-0.5 text-xs font-bold"
          style={{ background: "rgba(0,0,0,0.35)", color: "#fff" }}
        >
          ♥ 5/5
        </span>
      </div>

      <div className="px-4 pb-4" style={{ minHeight: 420 }}>
        {children}
      </div>

      <div className="px-4 pb-5">
        <div
          className="rounded-xl py-3 text-center text-sm font-extrabold"
          style={{ background: "#22C55E", color: "#fff" }}
        >
          Continue
        </div>
      </div>
      <div className="pb-1 text-center text-[9px]" style={{ color: C.inkDim }}>
        preview
      </div>
    </div>
  );
}
